import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    ArrowUpRight,
    Check,
    CheckCircle2,
    ChevronDown,
    Globe,
} from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { DEMO_SITES } from "@/lib/demo-sites";
import SiteScrollCarousel from "@/components/websites/SiteScrollCarousel";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
    "w-full bg-[#0E121B] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#FF3B30]/60 focus:ring-2 focus:ring-[#FF3B30]/20 transition-[border-color,box-shadow] duration-300";

const included = [
    "Custom one-page website",
    "Mobile-friendly design",
    "Professional modern layout",
    "Your branding, colours and logo",
    "Services or products",
    "Business information",
    "Photos",
    "Contact information",
    "Click-to-call functionality",
    "Social media links",
    "Location / Google Maps link",
    "Contact form when appropriate",
    "Basic search-engine-friendly page structure",
    "One revision round",
    "Launch assistance",
];

const steps = [
    {
        step: "01",
        title: "Tell Us About Your Business",
        copy: "Send us your business name, services, contact information, logo, photos and anything else customers should know.",
    },
    {
        step: "02",
        title: "We Build It",
        copy: "SalesCan turns that information into a polished, mobile-friendly website. Typical turnaround is 24–48 hours once we have everything we need.",
    },
    {
        step: "03",
        title: "Review It",
        copy: "Take a look at your completed website and send us one round of reasonable changes.",
    },
    {
        step: "04",
        title: "Go Live",
        copy: "Once everything looks right, we'll help get your new website online.",
    },
];

const upgrades = [
    {
        title: "Additional Pages",
        copy: "Add dedicated Services, About, Gallery, FAQ, Contact and other pages.",
        price: "from $49/page",
    },
    {
        title: "Booking Integration",
        copy: "Connect customers with your existing scheduling or booking system.",
        price: "from $49",
    },
    {
        title: "Google Business Profile Help",
        copy: "Improve the business information customers find through Google.",
        price: "from $49",
    },
    {
        title: "Website Care",
        copy: "Get ongoing help with updates and changes.",
        price: "from $29/month",
    },
    {
        title: "Custom Development",
        copy: "Need something more advanced? SalesCan can quote a larger solution separately.",
        price: "Quote",
    },
];

const faqs = [
    {
        q: "Is it really $99?",
        a: "Yes. The standard one-page SalesCan website is $99 CAD. Optional upgrades, third-party services, domains and functionality outside the standard package are separate.",
    },
    {
        q: "How quickly will I get my website?",
        a: "Our target is 24–48 hours after receiving the information and assets required to build your website.",
    },
    {
        q: "Do I need to know anything about websites?",
        a: "No. That's our job. If you can tell us about your business, we can handle the website.",
    },
    {
        q: "Do I need a domain?",
        a: "If you already have one, great. If you don't, we can help you get one. Domain registration and third-party fees are separate from the $99 website price.",
    },
    {
        q: "Can I make changes?",
        a: "Yes. Your website includes one revision round for reasonable changes to the initial build.",
    },
    {
        q: "What if I need a larger website?",
        a: "No problem. We can add additional pages or quote a larger custom project.",
    },
];

function CtaButton({ children, className = "", onClick, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick ?? (() => scrollToId("#get-started"))}
            className={`group inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#ff5147] text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300 shadow-[0_0_28px_rgba(255,59,48,0.35)] hover:shadow-[0_0_48px_rgba(255,59,48,0.6)] ${className}`}
        >
            {children}
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </button>
    );
}

function SectionLabel({ children }) {
    return (
        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-semibold mb-4">
            {children}
        </p>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-white/10">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-4 py-5 text-left"
            >
                <span className="font-display font-bold text-base sm:text-lg text-white">{q}</span>
                <ChevronDown
                    className={`w-5 h-5 shrink-0 text-slate-500 transition-transform duration-300 ${
                        open ? "rotate-180 text-[#FF3B30]" : ""
                    }`}
                />
            </button>
            {open && (
                <motion.p
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="pb-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-3xl"
                >
                    {a}
                </motion.p>
            )}
        </div>
    );
}

export default function WebsitesOfferPage() {
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        document.title = "SalesCan $99 Website — Get Your Business Online";
        return () => {
            document.title = "SalesCan — Dominate Your Pipeline with AI & Automation";
        };
    }, []);

    const onSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
    };

    return (
        <main data-testid="websites-offer-page">
            <section className="relative min-h-[90vh] flex items-center px-4 sm:px-8 lg:px-16 pt-28 pb-20 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(255,59,48,0.18),transparent)] pointer-events-none" />
                <div className="absolute -right-32 top-1/3 w-[480px] h-[480px] rounded-full bg-[#FF3B30]/10 blur-[120px] pointer-events-none" />

                <div className="relative max-w-[1400px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="lg:col-span-7"
                    >
                        <SectionLabel>SalesCan $99 Website</SectionLabel>
                        <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-7xl text-white leading-[0.95]">
                            Get Your Business
                            <br />
                            Online for <span className="text-stroke-red">$99.</span>
                        </h1>
                        <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-xl leading-relaxed">
                            A professional website. One simple price.
                        </p>
                        <p className="mt-4 text-base text-slate-400 max-w-xl leading-relaxed">
                            Get a modern, mobile-friendly website built for your business for just{" "}
                            <strong className="text-white font-semibold">$99 CAD</strong>.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm text-slate-400">
                            <li>No complicated packages.</li>
                            <li>No long-term contract.</li>
                            <li>No $2,000 website quote.</li>
                        </ul>
                        <p className="mt-4 text-base text-slate-300">
                            Just a great-looking business website — built fast.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                            <CtaButton testId="websites-hero-cta">Get My $99 Website</CtaButton>
                            <p className="text-xs font-mono-tech uppercase tracking-[0.18em] text-slate-500 max-w-xs leading-relaxed">
                                One-page website · One revision included · 24–48 hour target turnaround
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                        className="lg:col-span-5"
                    >
                        <div className="rounded-3xl border border-white/10 bg-[#0E121B]/75 backdrop-blur-2xl p-6 sm:p-8 shadow-2xl">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-full bg-[#FF3B30]/15 flex items-center justify-center">
                                    <Globe className="w-5 h-5 text-[#FF3B30]" />
                                </div>
                                <div>
                                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                        Example sites
                                    </p>
                                    <p className="text-sm font-semibold text-white">
                                        Scroll to watch them rotate
                                    </p>
                                </div>
                            </div>
                            <ul className="space-y-1">
                                {DEMO_SITES.map((site, index) => (
                                    <li key={site.id}>
                                        <button
                                            type="button"
                                            onClick={() => scrollToId("#example-sites")}
                                            className="flex w-full items-baseline justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-white/5"
                                        >
                                            <span className="font-mono-tech text-[10px] text-slate-600">
                                                {String(index + 1).padStart(2, "0")}
                                            </span>
                                            <span className="flex-1 text-sm text-slate-200">
                                                {site.name}
                                            </span>
                                            <span className="font-mono-tech text-[10px] uppercase tracking-[0.16em] text-slate-500">
                                                {site.industry}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-5 text-xs text-slate-500 leading-relaxed text-center">
                                Fictional examples of the $99 one-page build.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28 border-t border-white/5">
                <div className="max-w-[900px] mx-auto text-center">
                    <SectionLabel>The problem</SectionLabel>
                    <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[0.98]">
                        Your Business Deserves Better Than
                        <br />
                        <span className="text-stroke-red">"We Don't Have a Website."</span>
                    </h2>
                    <div className="mt-8 space-y-4 text-base sm:text-lg text-slate-400 leading-relaxed">
                        <p>Maybe you're just getting started.</p>
                        <p>Maybe your current website is years out of date.</p>
                        <p>
                            Or maybe you've been putting it off because getting a website sounds
                            expensive and complicated.
                        </p>
                        <p className="text-white font-semibold text-xl pt-2">We made it simple.</p>
                        <p>
                            For $99 CAD, SalesCan will build your business a clean, professional
                            one-page website designed to look great on phones and computers.
                        </p>
                        <p className="text-slate-300">You send us your business information. We build the website.</p>
                    </div>
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28 bg-[#0E121B]/40">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="lg:col-span-5"
                    >
                        <SectionLabel>What's included</SectionLabel>
                        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-white leading-[0.98]">
                            Everything You Need to Look
                            <br />
                            Professional Online
                        </h2>
                        <p className="mt-4 text-slate-400 leading-relaxed">
                            Included for <strong className="text-white">$99 CAD</strong>. That's it.
                        </p>
                        <div className="mt-8">
                            <CtaButton testId="websites-included-cta">Build My Website</CtaButton>
                        </div>
                    </motion.div>
                    <motion.ul
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                        className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3"
                    >
                        {included.map((item) => (
                            <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                                <Check className="w-4 h-4 text-[#FF3B30] shrink-0 mt-0.5" />
                                {item}
                            </li>
                        ))}
                    </motion.ul>
                </div>
            </section>

            <SiteScrollCarousel />

            <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28 border-t border-white/5">
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center mb-14">
                        <SectionLabel>How it works</SectionLabel>
                        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-white">
                            Four Simple Steps
                        </h2>
                        <p className="mt-4 text-slate-400 max-w-xl mx-auto">
                            No months-long project. No endless meetings. No technical headache.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {steps.map((item, i) => (
                            <motion.div
                                key={item.step}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                                className="rounded-2xl border border-white/10 bg-[#0E121B]/60 p-6"
                            >
                                <p className="font-mono-tech text-xs text-[#FF3B30] tracking-[0.25em] mb-3">
                                    {item.step}
                                </p>
                                <h3 className="font-display font-bold text-lg text-white mb-3">{item.title}</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">{item.copy}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-16 lg:py-20 bg-[#0E121B]/40">
                <div className="max-w-[800px] mx-auto text-center">
                    <h2 className="font-display font-bold text-2xl sm:text-3xl text-white">
                        Already Have a Website?
                    </h2>
                    <p className="mt-4 text-slate-400 leading-relaxed">
                        That's okay. The $99 Website is also perfect for businesses with an old,
                        outdated or ineffective website that simply needs a fresh start. Send us what
                        you currently have and we'll show your business in a more modern way.
                    </p>
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center mb-12">
                        <SectionLabel>Optional upgrades</SectionLabel>
                        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-white">
                            Need More Than One Page?
                        </h2>
                        <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
                            Start simple. Your $99 website can be expanded when your business needs
                            more. You only pay for what your business actually needs.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {upgrades.map((item) => (
                            <div
                                key={item.title}
                                className="rounded-2xl border border-white/10 bg-[#0E121B]/50 p-6 flex flex-col"
                            >
                                <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#FF3B30] mb-2">
                                    {item.price}
                                </p>
                                <h3 className="font-display font-bold text-lg text-white mb-2">{item.title}</h3>
                                <p className="text-sm text-slate-400 leading-relaxed flex-1">{item.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28 border-t border-white/5">
                <div className="max-w-[800px] mx-auto">
                    <SectionLabel>Why $99?</SectionLabel>
                    <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-white leading-[0.98]">
                        Because Building Websites Has Changed.
                    </h2>
                    <div className="mt-6 space-y-4 text-slate-400 leading-relaxed">
                        <p>
                            Modern development tools allow us to create professional small-business
                            websites dramatically faster than traditional web-development workflows.
                        </p>
                        <p>
                            We don't believe every local business needs a massive agency project. So
                            instead of charging thousands for something simple, we've created a
                            streamlined website package for businesses that just need to look
                            professional and get online.
                        </p>
                        <p className="text-white font-semibold">Simple scope. Fast production. Fair price.</p>
                    </div>
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28 bg-[#0E121B]/40">
                <div className="max-w-[800px] mx-auto">
                    <SectionLabel>FAQ</SectionLabel>
                    <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-white mb-8">
                        Frequently Asked Questions
                    </h2>
                    {faqs.map((item) => (
                        <FaqItem key={item.q} q={item.q} a={item.a} />
                    ))}
                </div>
            </section>

            <section
                id="get-started"
                data-testid="websites-intake-section"
                className="relative px-4 sm:px-8 lg:px-16 py-24 lg:py-36 overflow-hidden"
            >
                <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-[#FF3B30]/8 blur-[160px] pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="lg:col-span-6"
                    >
                        <SectionLabel>Get started</SectionLabel>
                        <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[0.95]">
                            Stop Putting Off
                            <br />
                            <span className="text-stroke-red">Your Website.</span>
                        </h2>
                        <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-lg">
                            Your customers are already looking for businesses online. Give them
                            something professional to find.
                        </p>
                        <p className="mt-4 text-lg text-white font-semibold">
                            Your new business website. Built fast. $99 CAD.
                        </p>
                        <p className="mt-6 text-xs font-mono-tech uppercase tracking-[0.18em] text-slate-500">
                            No long-term contract · One revision included · 24–48 hour target turnaround
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                        className="lg:col-span-6"
                    >
                        <div className="rounded-3xl border border-white/10 bg-[#0E121B]/75 backdrop-blur-2xl p-8 sm:p-10 shadow-2xl">
                            {submitted ? (
                                <div data-testid="websites-form-success" className="py-16 text-center">
                                    <CheckCircle2 className="w-12 h-12 text-[#FF3B30] mx-auto mb-5" />
                                    <h3 className="font-display font-bold text-2xl text-white">Request received</h3>
                                    <p className="mt-3 text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">
                                        Thanks — we'll follow up shortly with next steps and payment
                                        details for your $99 website build.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label
                                                htmlFor="website-name"
                                                className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                            >
                                                Your name
                                            </label>
                                            <input
                                                id="website-name"
                                                data-testid="websites-form-name-input"
                                                required
                                                placeholder="Jane Tremblay"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="website-email"
                                                className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                            >
                                                Email
                                            </label>
                                            <input
                                                id="website-email"
                                                data-testid="websites-form-email-input"
                                                type="email"
                                                required
                                                placeholder="jane@business.ca"
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="website-business"
                                            className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                        >
                                            Business name
                                        </label>
                                        <input
                                            id="website-business"
                                            data-testid="websites-form-business-input"
                                            required
                                            placeholder="Acme Plumbing"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="website-phone"
                                            className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                        >
                                            Phone
                                        </label>
                                        <input
                                            id="website-phone"
                                            data-testid="websites-form-phone-input"
                                            type="tel"
                                            required
                                            placeholder="(905) 555-0100"
                                            className={inputCls}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        data-testid="websites-form-submit-button"
                                        className="group w-full flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#ff5147] text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300 shadow-[0_0_28px_rgba(255,59,48,0.35)] hover:shadow-[0_0_48px_rgba(255,59,48,0.6)]"
                                    >
                                        Get Started for $99
                                        <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                                    </button>
                                    <p className="text-center text-xs text-slate-500">
                                        $99 CAD upfront before build begins. We'll confirm your details
                                        before payment.
                                    </p>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </div>
            </section>
        </main>
    );
}
